import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, Expand, Focus, HelpCircle, Pause, Play, RefreshCcw, Search, ShieldCheck } from 'lucide-react';
import { scenarios, scenarioById } from '../data/scenarios';
import { tr } from '../i18n';
import { searchNodes } from '../lib/graph';
import { useDiagramStore } from '../state/store';

interface ToolbarProps {
  onFitView: () => void;
  onFullscreen: () => void;
  onExport: () => void;
  onSearchSelect: (id: string) => void;
  fullscreen: boolean;
}

export function Toolbar({ onFitView, onFullscreen, onExport, onSearchSelect, fullscreen }: ToolbarProps) {
  const state = useDiagramStore();
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const results = useMemo(() => searchNodes(query, state.language).slice(0, 9), [query, state.language]);
  const playing = state.status === 'playing';
  const scenario = scenarioById.get(state.scenarioId)!;

  useEffect(() => {
    const onFocusSearch = () => { state.setSearchOpen(true); requestAnimationFrame(() => inputRef.current?.focus()); };
    window.addEventListener('pfd:focus-search', onFocusSearch);
    return () => window.removeEventListener('pfd:focus-search', onFocusSearch);
  }, [state]);

  return (
    <header className="toolbar">
      <div className="brand-lockup">
        <span className="brand-kicker">{tr(state.language, 'appKicker')}</span>
        <h1>{tr(state.language, 'appTitle')}</h1>
      </div>
      <div className="toolbar-actions" role="toolbar" aria-label={state.language === 'en' ? 'Presentation controls' : 'Təqdimat idarəetmələri'}>
        <label className="select-control funnel-select">
          <span className="sr-only">{tr(state.language, 'funnel')}</span>
          <select value={state.funnelFilter} onChange={(event) => state.setFunnelFilter(event.target.value as 'all' | 'tofu' | 'mofu' | 'bofu')} aria-label={tr(state.language, 'funnel')}>
            <option value="all">{tr(state.language, 'allFunnel')}</option>
            <option value="tofu">TOFU · {tr(state.language, 'awareness')}</option>
            <option value="mofu">MOFU · {tr(state.language, 'consideration')}</option>
            <option value="bofu">BOFU · {tr(state.language, 'conversion')}</option>
          </select>
        </label>
        <label className="select-control scenario-select">
          <span className="sr-only">Scenario</span>
          <select value={state.scenarioId} onChange={(event) => state.setScenario(event.target.value)} aria-label="Scenario">
            {scenarios.map((item) => <option value={item.id} key={item.id}>{item.shortTitle[state.language]}</option>)}
          </select>
          <small>{state.stepIndex + 1}/{scenario.steps.length}</small>
        </label>
        <div className="button-cluster playback-cluster">
          <button className="icon-button primary" onClick={playing ? state.pause : state.play} aria-label={tr(state.language, playing ? 'pause' : 'play')} title={tr(state.language, playing ? 'pause' : 'play')}>
            {playing ? <Pause size={17} /> : <Play size={17} />}
          </button>
          <button className="icon-button" onClick={state.restart} aria-label={tr(state.language, 'restart')} title={tr(state.language, 'restart')}><RefreshCcw size={16} /></button>
          <button className="icon-button" onClick={state.previous} aria-label={tr(state.language, 'previous')} title={tr(state.language, 'previous')}><ChevronLeft size={17} /></button>
          <button className="icon-button" onClick={state.next} aria-label={tr(state.language, 'next')} title={tr(state.language, 'next')}><ChevronRight size={17} /></button>
        </div>
        <label className="select-control speed-select">
          <span className="sr-only">{tr(state.language, 'speed')}</span>
          <select value={state.speed} onChange={(event) => state.setSpeed(Number(event.target.value))} aria-label={tr(state.language, 'speed')}>
            {[0.5, 1, 1.5, 2].map((speed) => <option key={speed} value={speed}>{speed}x</option>)}
          </select>
        </label>
        <div className="button-cluster view-cluster">
          <button className="icon-button" onClick={onFitView} aria-label={tr(state.language, 'fitView')} title={tr(state.language, 'fitView')}><Focus size={16} /></button>
          <button className="icon-button" onClick={onFullscreen} aria-label={tr(state.language, fullscreen ? 'exitFullscreen' : 'fullscreen')} title={tr(state.language, fullscreen ? 'exitFullscreen' : 'fullscreen')}><Expand size={16} /></button>
          <button className="icon-button" onClick={onExport} aria-label={tr(state.language, 'exportPng')} title={tr(state.language, 'exportPng')}><Download size={16} /></button>
        </div>
        <div className="search-control">
          <button className="icon-button" onClick={() => { state.setSearchOpen(!state.searchOpen); requestAnimationFrame(() => inputRef.current?.focus()); }} aria-label={tr(state.language, 'searchButton')}><Search size={17} /></button>
          {state.searchOpen && (
            <div className="search-popover">
              <div className="search-input-wrap"><Search size={15} /><input ref={inputRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder={tr(state.language, 'search')} aria-label={tr(state.language, 'search')} /></div>
              {query && <div className="search-results" role="listbox">
                {results.map((node) => (
                  <button key={node.id} role="option" onClick={() => { onSearchSelect(node.id); setQuery(''); state.setSearchOpen(false); }}>
                    <strong>{node.title[state.language]}</strong><small>{node.summary[state.language]}</small>
                  </button>
                ))}
                {!results.length && <p>{tr(state.language, 'noResults')}</p>}
              </div>}
            </div>
          )}
        </div>
        <button className={`toggle-button ${state.roleLens ? 'is-on' : ''}`} onClick={() => state.setRoleLens(!state.roleLens)} aria-pressed={state.roleLens} aria-label={tr(state.language, 'roleLens')}>
          <ShieldCheck size={15} /><span>{tr(state.language, 'roleLens')}</span>
        </button>
        <button className="icon-button" onClick={() => state.setSourcesOpen(true)} aria-label={tr(state.language, 'sources')} title={tr(state.language, 'sources')}><HelpCircle size={17} /></button>
        <div className="language-switch" aria-label={tr(state.language, 'language')}>
          {(['en', 'az'] as const).map((language) => <button key={language} className={state.language === language ? 'active' : ''} onClick={() => state.setLanguage(language)}>{language.toUpperCase()}</button>)}
        </div>
      </div>
    </header>
  );
}
